<script setup lang="ts">
import { API } from '@vp/common';
import { account, staff, VPConfirmationDialogHost, VPLoadingSpinner, permissions } from '@vp/vue-components';
import { onMounted, ref } from 'vue';
import { useRootStore } from '@/stores/root';
import { useAccountStore } from '@/stores/account';
import { useGettext } from 'vue3-gettext';
import { useConnectionStore } from '@/stores/connection';
import * as Sentry from '@sentry/vue';

const loading = ref(false);
const rootStore = useRootStore();
const accountStore = useAccountStore();
const organizationStore = account.useOrganizationStore({ fetchOrganizations: API.App.Account.Api.fetchOrganizations }, 'standalone-org');
const staffStore = staff.useStaffStore();
const connectionStore = useConnectionStore();
const { ensurePermissions } = permissions.usePolicy();

const { $gettext } = useGettext();

/**
 * Work out which account this session is in, and tell the connection store — before anything reads
 * account-scoped data.
 *
 * The ordering is the fix, not just the assignment. Discovery and the account config used to be
 * awaited together and the account assigned afterwards, so the config read and the permissions
 * fetch went out with no `X-VP-Account-Id` and were answered by an arbitrary account.
 *
 * The account is deliberately left unresolved rather than seeded from the stored selection: the
 * connection store holds an account-scoped request issued in the meantime and releases it once the
 * account is known, and `installPlugins` fires exactly one such request — the permissions fetch,
 * which loads once per session. Seeding would release it against a stored id that discovery is
 * about to discard, and nothing would ever ask again.
 */
const resolveAccount = async () => {
  try {
    // Read before the load: `setDefault` resets the stored selection whenever the id is absent from
    // the returned list.
    const storedAccountId = organizationStore.selectedAccountId;

    // Account-agnostic by construction: the Cognito config is read before login, and asking which
    // accounts exist cannot be scoped to one of them.
    await Promise.all([
      rootStore.loadConfig(),
      organizationStore.loadOrganizations({ setDefault: true }),
    ]);

    // Switching accounts reloads the page, which lands back here. A VenuePilot staff session can be
    // impersonating an account outside its own organizations — which the tenant list cannot see, so
    // `setDefault` would replace it with the operator's own default and undo the very switch this
    // reload is applying. Restore what they chose; a tenant's stale id is a different case and
    // `setDefault` is still the right answer for it.
    //
    // The staff probe is asked for only once `setDefault` has actually discarded a stored id, and
    // only then. It has to be awaited here
    // rather than deferred: `accountId` below is what every account-scoped query reads, so
    // resolving it late would fire the first page of them against the wrong account.
    if (storedAccountId && organizationStore.selectedAccountId !== storedAccountId) {
      await staffStore.ensureLoaded();

      if (staffStore.isStaff) {
        organizationStore.setSelectedAccountId(storedAccountId);
      }
    }

    // `null`, not `undefined`, when there is nothing to select: an operator who belongs to no
    // organization has a settled answer, and the connection store fails their account-scoped reads
    // immediately instead of holding each one until its timeout.
    connectionStore.accountId = organizationStore.selectedAccountId ?? null;
  } catch (e) {
    // Discovery failed, so the account is not going to be resolved. Say so rather than leaving the
    // ref unresolved, which would make every later request wait out the connection store's timeout
    // before reporting the same thing.
    //
    // Reported rather than rethrown: an `onMounted` callback has nobody to rethrow to, so this
    // used to escape as an unhandled rejection, picked up — if Sentry had finished initialising —
    // by the window handler in `main.app.ts`.
    connectionStore.accountId = null;
    Sentry.captureException(e);
  }
};

onMounted(async () => {
  try {
    loading.value = true;

    await resolveAccount();

    // Account-scoped, and now correctly scoped — this is the whole point of the ordering. These are
    // awaited separately from the resolution above so that an ordinary read failure cannot be
    // mistaken for an answer about which account the operator is in.
    try {
      await Promise.all([accountStore.loadAccountConfig(), ensurePermissions()]);
    } catch (e) {
      Sentry.captureException(e);
    }
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div id="vp-teleport-vp-modal" />
  <VPConfirmationDialogHost />
  <VPLoadingSpinner
    :loading="loading"
    :bottom-message="$gettext('Initializing...')"
    class="h-screen w-screen"
  >
    <div class="h-screen w-screen">
      <RouterView />
    </div>
  </VPLoadingSpinner>
</template>
